import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, ChevronDown, TrendingUp, Building2, Globe2, ShieldCheck, HandCoins, BarChart3, Compass, Network, Handshake, Camera, ScanLine, FileEdit, Eye, FileCheck2, MailCheck, Users, Briefcase, Zap, Target } from "lucide-react";
import { NetworkCanvas } from "../components/NetworkCanvas";
import { useAuth } from "../context/AuthContext";
import { SectionHeading, MatchRing, FitBar, VerifiedBadge, QRPattern } from "../components/kit";
import { OpportunityCard, CompanyCard, EventCard } from "../components/cards";
import { api } from "../lib/api";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const PORTAL_IMG = "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/ff640350733e69577152125b02dc9d0ce10681febdf469468344f792c14c7ce6.jpeg";
const SCAN_REG_IMG = "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/81b8a3c298937b6546d4a4ce5ca394ed64b6453a04c89fc64228f381353d006e.jpeg";

const HERO_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/eb9f9c79796094740514fbd00a5c16d22b7f028cddd8212607e07e8d4e701089.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/c0db01636a90231383f6442a44ff94d494c60491dbdd62c778d57e5ca6ff3041.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/7a22c0ac1c0de3f79221c5567310319a74a1536c3e8c6d3d806e7cf6f528c613.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/4c228bc7078068a6b5937bba6c3595de3b8bc80af57dc9db14d60911dd38d5e7.jpeg",
];

const SOLUTIONS = [
  { icon: Network, title: "Smart Matching", desc: "AI-powered matching to connect with the right partners, buyers and opportunities.", href: "/opportunities" },
  { icon: HandCoins, title: "Capital Solutions", desc: "Access equity, debt, structured capital and investors for your business growth.", href: "/opportunities?type=FUNDING%20REQUIREMENT" },
  { icon: BarChart3, title: "Business Intelligence", desc: "Real-time market insights, industry intelligence and growth opportunities that matter.", href: "/intelligence" },
  { icon: Globe2, title: "Global Expansion", desc: "Expand your business globally with strategic partnerships and market access.", href: "/industries" },
  { icon: Compass, title: "Project & Growth Advisory", desc: "From strategy to execution, we help you build, scale and lead.", href: "/contact" },
];

const OFFER = ["Products", "Services", "Technology", "Manufacturing", "Distribution", "Consulting", "Finance"];
const NEED = ["Buyers", "Suppliers", "Investors", "Funding", "Distributors", "Technology", "Partners", "Projects", "Raw Materials", "Export Opportunities"];

const REGION_NAMES = ["India", "Middle East", "Southeast Asia", "Europe", "USA", "Africa", "Australia"];

function HeroSearch() {
  const [value, setValue] = useState("");
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-xl" data-testid="hero-search">
      <p className="gold-kicker mb-3">What are you looking for?</p>
      <form onSubmit={(e) => { e.preventDefault(); navigate(`/opportunities?q=${encodeURIComponent(value || "partners")}`); }}
        className="flex flex-col sm:flex-row sm:items-stretch bg-navy-900/95 border border-slate-700 rounded-sm overflow-hidden focus-within:border-gold transition-colors shadow-2xl">
        <div className="hidden sm:flex pl-4 items-center text-slate-500"><Search size={18} /></div>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Find partners, buyers, suppliers, or investors"
          data-testid="hero-ai-search-input"
          className="flex-1 w-full sm:w-auto min-w-0 bg-transparent px-4 py-4 text-sm md:text-base text-slate-100 placeholder:text-slate-500 focus:outline-none transition-all" />
        <button type="submit" data-testid="find-matches-btn" className="btn-gold !rounded-none w-full sm:w-auto !px-4 md:!px-8 !py-3.5 text-xs md:text-sm whitespace-nowrap">FIND MATCHES</button>
      </form>
      <p className="mt-3 text-[11px] font-mono text-slate-600">AI turns your request into structured business matches.</p>
    </div>
  );
}

export default function Home() {
  const [opportunities, setOpportunities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [events, setEvents] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [intel, setIntel] = useState(null);
  const [feed, setFeed] = useState([]);
  const [pubStats, setPubStats] = useState(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const intentClick = (kind, item) => {
    if (user) navigate(`/rfq?${kind}=${encodeURIComponent(item)}`);
    else navigate("/register");
  };

  const guardReg = (href) => (user ? href : "/register");

  const [heroSlide, setHeroSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroSlide((s) => (s + 1) % 4), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/opportunities").then((r) => setOpportunities(r.data.slice(0, 4))).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {});
    api.get("/intelligence/summary").then((r) => setIntel(r.data)).catch(() => {});
    api.get("/feed").then((r) => setFeed(r.data)).catch(() => {});
    api.get("/stats/public").then((r) => setPubStats(r.data)).catch(() => {});
  }, []);

  const apex = companies[0];

  return (
    <div data-testid="home-page">
      {/* HERO — background-only slider + fixed foreground content */}
      <section className="relative overflow-hidden h-[560px] sm:h-[500px] lg:h-[520px] [@media(max-height:500px)]:h-[380px]" data-testid="hero-section">
        {/* Background slider layer — images only */}
        <div className="absolute inset-0 flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${heroSlide * 100}%)` }} data-testid="hero-slider-track">
          {HERO_IMAGES.map((img, i) => (
            <img key={i} src={img} alt="" aria-hidden="true" className="w-full h-full shrink-0 object-cover object-center" data-testid={`hero-bg-${i}`} />
          ))}
        </div>
        {/* Readability overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/95 via-navy-950/65 to-navy-950/25 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-navy-950/30 pointer-events-none" />
        {/* Foreground content — fixed, never slides */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col justify-center pb-12" data-testid="hero-foreground">
          <h1 className="font-display font-black tracking-tight leading-[1.02] animate-fade-up max-sm:landscape:text-3xl text-4xl sm:text-5xl lg:text-6xl text-slate-100" data-testid="hero-headline">
            CONNECT. COLLABORATE. <span className="text-techblue-light">GROW.</span>
          </h1>
          <p className="mt-4 text-sm md:text-base text-slate-300 leading-relaxed max-w-md animate-fade-up stagger-1" data-testid="hero-sub">
            Where verified businesses meet real opportunities.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 animate-fade-up stagger-2">
            <Link to="/members" data-testid="explore-network-btn" className="btn-gold !px-7 !py-3.5 text-sm">EXPLORE NETWORK <ArrowRight size={16} /></Link>
            <Link to="/opportunities" data-testid="explore-opportunities-btn" className="btn-outline !px-7 !py-3.5 text-sm">EXPLORE OPPORTUNITIES <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="hero-slider-dots">
          {[0, 1, 2, 3].map((i) => (
            <button key={i} onClick={() => setHeroSlide(i)} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${heroSlide === i ? "w-8 bg-gold" : "w-4 bg-slate-500/60 hover:bg-slate-400"}`} />
          ))}
        </div>
      </section>

      {/* SEARCH — fixed strip directly below the hero slider */}
      <section className="border-b border-slate-800 bg-navy-900/40" data-testid="hero-search-section">
        <div className="max-w-xl mx-auto px-4 md:px-8 py-5 md:py-6">
          <div className="bg-navy-950/70 backdrop-blur-md border border-slate-700/60 rounded-2xl px-4 py-3.5">
            <HeroSearch />
          </div>
        </div>
      </section>

      {/* HERO FEATURES STRIP */}
      <section className="border-b border-slate-800 bg-navy-900/40" data-testid="hero-features-strip">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Globe2, title: "GLOBAL NETWORK", desc: "Verified Businesses", key: "global-network" },
            { icon: Handshake, title: "STRATEGIC PARTNERSHIPS", desc: "Build meaningful connections", key: "strategic-partnerships" },
            { icon: TrendingUp, title: "BUSINESS GROWTH", desc: "Unlock new markets and opportunities", key: "business-growth" },
            { icon: ShieldCheck, title: "TRUST & SECURITY", desc: "A secure and transparent business ecosystem", key: "trust-security" },
          ].map((f) => (
            <div key={f.key} className="flex flex-col items-center text-center gap-3" data-testid={`hero-feature-${f.key}`}>
              <div className="w-12 h-12 rounded-full border border-techblue/40 bg-navy-950 flex items-center justify-center">
                <f.icon size={20} className="text-techblue-light" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-wide text-slate-100">{f.title}</p>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 10X FASTER REGISTRATION — SCAN BANNER */}
      <section className="border-b border-slate-800" data-testid="fast-registration-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="gold-kicker mb-4">One Scan. Limitless Possibilities.</p>
            <h2 className="font-display font-black tracking-tight leading-[1.02]" data-testid="fast-reg-headline">
              <span className="text-slate-100 text-4xl sm:text-5xl">10X </span>
              <span className="text-gold text-4xl sm:text-5xl">FASTER</span>
              <span className="block text-slate-100 text-4xl sm:text-5xl mt-1">REGISTRATION</span>
            </h2>
            <p className="mt-4 text-sm md:text-base"><span className="text-slate-100 font-semibold">One Scan.</span> <span className="text-techblue-light font-semibold">Instant Connection.</span></p>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-md">Scan your visiting card and register instantly. Connect, collaborate and grow together.</p>
            <Link to="/scan" data-testid="fast-reg-scan-btn" className="btn-gold mt-7 !px-7 !py-3.5 text-sm"><Camera size={16} /> SCAN CARD</Link>
          </div>
          <div className="hidden lg:block">
            <img src={SCAN_REG_IMG} alt="Scan your visiting card to register instantly" className="w-full max-w-md mx-auto rounded-sm border border-slate-800" data-testid="fast-reg-img" />
          </div>
        </div>
        <div className="border-t border-slate-800/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
            <p className="text-center text-xs font-mono uppercase tracking-[0.3em] text-gold mb-8">How It Works</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="fast-reg-steps">
              {[[Camera, "SCAN", "Scan the visiting card using your camera."],
                [ScanLine, "EXTRACT", "AI reads and extracts card information."],
                [FileEdit, "AUTO FILL", "We auto-fill the registration form."],
                [Eye, "REVIEW", "Review the details and make quick edits."],
                [FileCheck2, "SUBMIT", "Submit the form and you're registered."],
                [MailCheck, "CONFIRMATION", "You'll receive a confirmation email."]].map(([Icon, t, d], i) => (
                <div key={t} className="card-surface p-4 text-center relative" data-testid={`fast-reg-step-${i + 1}`}>
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-techblue text-white font-mono text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                  <Icon size={18} className="text-techblue-light mx-auto mt-2 mb-2" />
                  <p className="text-[10.5px] font-bold tracking-wide text-slate-100">{t}</p>
                  <p className="text-[10px] text-slate-500 mt-1 leading-snug">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800/70 bg-navy-900/40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="fast-reg-benefits">
            {[[Zap, "INSTANT REGISTRATION", "One scan, get started in seconds."],
              [Users, "STRONGER CONNECTIONS", "Connect with verified professionals."],
              [TrendingUp, "MORE OPPORTUNITIES", "Discover deals, collaborations & business growth."],
              [ShieldCheck, "TRUSTED NETWORK", "Verified members. Secure platform."]].map(([Icon, t, d]) => (
              <div key={t} className="flex items-start gap-3" data-testid={`fast-reg-benefit-${t.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="w-10 h-10 rounded-full border border-techblue/40 bg-navy-950 flex items-center justify-center shrink-0"><Icon size={17} className="text-techblue-light" /></span>
                <div>
                  <p className="text-[11px] font-bold tracking-wide text-slate-100">{t}</p>
                  <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20" data-testid="solutions-section">
        <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-start">
          <div>
            <p className="gold-kicker mb-4">— Our Solutions</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Intelligent. Integrated.<br /><span className="text-techblue-light">Impactful.</span></h2>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">Next-generation solutions and deep industry intelligence to help businesses connect, collaborate and scale beyond boundaries.</p>
            <Link to="/opportunities" data-testid="view-all-solutions-btn" className="btn-outline mt-6 text-xs">VIEW ALL SOLUTIONS <ArrowRight size={14} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
            {SOLUTIONS.map((s) => (
              <Link key={s.title} to={guardReg(s.href)} data-testid={`solution-card-${s.title.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className="card-surface hairline-gold p-5 flex flex-col gap-3 hover:border-gold/50 transition-colors group">
                <s.icon size={22} className="text-techblue-light" />
                <p className="font-display font-bold text-slate-100 text-sm uppercase tracking-wide">{s.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed flex-1">{s.desc}</p>
                <ArrowRight size={16} className="text-gold group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENCE */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="difference-section">
        <SectionHeading kicker="The 10XGLOBAL Difference" title={"NOT ANOTHER BUSINESS DIRECTORY."}
          sub="A business intelligence layer connecting what companies ARE with what companies NEED." testid="difference-heading" />
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <div className="card-surface p-8 opacity-70" data-testid="traditional-directory-card">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-6">Traditional Directory</p>
            <ul className="space-y-4">
              {["Company Name", "Phone", "Website", "Products"].map((x) => (
                <li key={x} className="text-slate-500 text-sm border-b border-slate-800 pb-3">{x}</li>
              ))}
            </ul>
          </div>
          <div className="card-surface hairline-gold p-8" data-testid="tenx-platform-card">
            <p className="text-xs font-mono uppercase tracking-widest text-gold mb-6">10XGLOBAL</p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-4">
              {["Verified Identity", "Capabilities", "Decision Makers", "Business Intent", "Requirements", "Opportunities", "AI Matches", "Connections", "Lead Intelligence"].map((x) => (
                <li key={x} className="text-slate-200 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />{x}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-center mt-14 font-display text-xl md:text-2xl font-bold text-slate-100">
          WE DON'T JUST SHOW BUSINESSES.<br /><span className="text-gold">WE HELP BUSINESSES FIND EACH OTHER.</span>
        </p>
      </section>

      {/* INTENT ENGINE */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="intent-engine-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <SectionHeading kicker="The Business Intent Engine" title={"TELL US WHAT YOU OFFER.\nTELL US WHAT YOU NEED."}
            sub="The AI Matching Engine connects intent on both sides of the network." testid="intent-engine-heading" />
          <div className="grid md:grid-cols-[1fr_auto_1fr] gap-8 items-center max-w-6xl mx-auto">
            <div className="card-surface p-6 md:p-8" data-testid="intent-offer-panel">
              <p className="gold-kicker mb-5">I OFFER</p>
              <div className="flex flex-wrap gap-2">
                {OFFER.map((o) => (
                  <button key={o} onClick={() => intentClick("offer", o)} data-testid={`offer-pill-${o.toLowerCase()}`} className="border border-techblue/40 text-techblue-light text-xs font-medium rounded-sm px-3 py-1.5 hover:bg-techblue/10 transition-colors cursor-pointer">{o}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 px-2" data-testid="ai-matching-core">
              <div className="hidden md:block w-16 h-px bg-gradient-to-r from-techblue to-gold" />
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gold/20 animate-pulse-ring" />
                <div className="relative w-24 h-24 rounded-full bg-navy-950 border-2 border-gold flex flex-col items-center justify-center">
                  <span className="font-display font-black text-gold text-lg leading-none">AI</span>
                  <span className="text-[8px] font-mono uppercase tracking-widest text-slate-400 mt-1 text-center leading-tight">Matching<br />Engine</span>
                </div>
              </div>
              <div className="hidden md:block w-16 h-px bg-gradient-to-l from-techblue to-gold" />
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-2">Live RFQ matching — powered by 10X AI</p>
            </div>
            <div className="card-surface p-6 md:p-8" data-testid="intent-need-panel">
              <p className="gold-kicker mb-5">I NEED</p>
              <div className="flex flex-wrap gap-2">
                {NEED.map((n) => (
                  <button key={n} onClick={() => intentClick("need", n)} data-testid={`need-pill-${n.toLowerCase().replace(/\s/g, "-")}`} className="border border-gold/40 text-gold text-xs font-medium rounded-sm px-3 py-1.5 hover:bg-gold/10 transition-colors cursor-pointer">{n}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI MATCHING */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="ai-matching-section">
        <SectionHeading kicker="AI Business Matching" title={"FIND THE RIGHT BUSINESS.\nBEFORE THE OPPORTUNITY PASSES."} testid="ai-matching-heading" />
        <div className="grid lg:grid-cols-5 gap-6 max-w-6xl mx-auto items-start">
          <div className="lg:col-span-2 card-surface p-6 md:p-8" data-testid="match-requirement-panel">
            <p className="gold-kicker mb-5">Requirement</p>
            <dl className="space-y-4 text-sm">
              {[["Category", "Commercial Kitchen Equipment"], ["Location", "Bangalore"], ["Project", "12 hospitality properties"], ["Timeline", "90 days"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-800 pb-3">
                  <dt className="text-slate-500">{k}</dt><dd className="text-slate-200 font-medium text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> AI matching in progress — 128 verified businesses evaluated
            </div>
          </div>
          <div className="lg:col-span-3 card-surface hairline-gold p-6 md:p-8" data-testid="top-match-card">
            <div className="flex flex-wrap items-center gap-6">
              <MatchRing value={96} testid="top-match-ring" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-1">Top Match</p>
                <h3 className="font-display text-xl font-bold text-slate-100">{apex?.name || "Apex Kitchen Systems Pvt Ltd"}</h3>
                <p className="text-xs text-slate-500 mt-1">Verified Manufacturer · Bangalore, India</p>
                <div className="mt-2"><VerifiedBadge level="TRUSTED BUSINESS" testid="top-match-badge" /></div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-7">
              <FitBar label="Product Fit" value={98} />
              <FitBar label="Location Fit" value={94} />
              <FitBar label="Industry Fit" value={97} />
              <FitBar label="Intent Fit" value={95} />
            </div>
            <button onClick={() => setWhyOpen(!whyOpen)} data-testid="why-match-toggle"
              className="mt-6 flex items-center gap-2 text-xs font-semibold text-techblue-light hover:text-slate-200">
              Why this match? <ChevronDown size={14} className={`transition-transform ${whyOpen ? "rotate-180" : ""}`} />
            </button>
            {whyOpen && (
              <div className="mt-3 border-l-2 border-gold/50 pl-4 py-1 text-sm text-slate-400 leading-relaxed" data-testid="why-match-panel">
                This manufacturer holds TRUSTED BUSINESS verification, has delivered 12+ hospitality projects in Bangalore within 90-day windows, maintains SS-304 in-house fabrication, and its stated business intent matches your procurement category with 95% confidence.
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to={guardReg("/opportunities")} data-testid="match-view-profile-btn" className="btn-gold !px-4 !py-2 text-xs">EXPLORE MATCHES</Link>
              <Link to={guardReg("/rfq")} data-testid="match-request-quote-btn" className="btn-outline !px-4 !py-2 text-xs">REQUEST QUOTE</Link>
            </div>
          </div>
        </div>
      </section>

      {/* VERIFIED IDENTITY */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="verified-identity-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="gold-kicker mb-4">Verified Business Identity</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">YOUR BUSINESS.<br />YOUR VERIFIED DIGITAL IDENTITY.</h2>
            <p className="mt-5 text-slate-400 leading-relaxed">The 10XGLOBAL Business ID transforms the traditional membership certificate into a living digital identity — verified, scannable and trusted across the network.</p>
            <div className="mt-8 space-y-3" data-testid="verification-levels">
              {[
                ["REGISTERED", "Business record created and email/domain confirmed", "text-slate-400 border-slate-600"],
                ["BUSINESS VERIFIED", "Registration documents and tax identity checked", "text-techblue-light border-techblue/40"],
                ["PROFILE VERIFIED", "Capabilities, facilities and references validated", "text-techblue-light border-techblue"],
                ["TRUSTED BUSINESS", "Multi-source verification + sustained network reputation", "text-gold border-gold/50"],
              ].map(([lvl, desc, cls]) => (
                <div key={lvl} className={`border rounded-sm px-4 py-3 flex items-center justify-between gap-4 ${cls}`} data-testid={`verification-level-${lvl.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="text-xs font-mono uppercase tracking-widest">{lvl}</span>
                  <span className="text-xs text-slate-500 text-right">{desc}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[11px] text-slate-600 leading-relaxed">Verification confirms the identity attributes listed for each level. It does not guarantee a company's financial performance, legality or creditworthiness unless separately verified.</p>
            <Link to="/join" data-testid="create-business-id-cta" className="btn-gold mt-7">CREATE BUSINESS ID <ArrowRight size={16} /></Link>
          </div>
          <div className="card-surface hairline-gold p-8 max-w-md w-full mx-auto" data-testid="business-id-card">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-sm bg-gold flex items-center justify-center font-display font-black text-navy-950 text-xs">10X</span>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">10XGLOBAL Business ID</p>
                  <p className="font-mono text-xs text-gold">10X-IN-KA-00847</p>
                </div>
              </div>
              <ShieldCheck size={22} className="text-gold" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-100">Apex Kitchen Systems Pvt Ltd</h3>
            <p className="text-xs text-slate-500 mt-1">Hospitality · Commercial Kitchen Equipment · Bangalore, India</p>
            <div className="flex items-end justify-between mt-7">
              <div className="space-y-3 text-xs">
                <div><p className="text-slate-500 font-mono uppercase tracking-widest text-[9px]">Verification Level</p><p className="text-gold font-semibold mt-0.5">TRUSTED BUSINESS</p></div>
                <div><p className="text-slate-500 font-mono uppercase tracking-widest text-[9px]">Profile Strength</p>
                  <div className="w-36 h-1.5 bg-navy-700 rounded-full mt-1 overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: "94%" }} /></div>
                </div>
                <p className="text-slate-500 font-mono text-[10px]">Profile verified 12 days ago</p>
              </div>
              <QRPattern testid="business-id-qr" />
            </div>
          </div>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="opportunities-section">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="gold-kicker mb-4">Opportunity Intelligence</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">BUSINESS OPPORTUNITIES<br />THAT MATCH YOUR BUSINESS.</h2>
          </div>
          <Link to="/opportunities" data-testid="view-all-opportunities-btn" className="btn-outline">VIEW ALL OPPORTUNITIES <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {opportunities.map((o) => <OpportunityCard key={o.id} opp={o} compact />)}
        </div>
      </section>

      {/* AI FEED */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="opportunity-feed-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="gold-kicker mb-4">AI Opportunity Feed</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">YOUR BUSINESS<br />OPPORTUNITY FEED</h2>
            <p className="mt-5 text-slate-400 leading-relaxed">Personalized intelligence — every notification explains why it matters to your business.</p>
            <Link to="/join" className="btn-outline mt-8">ACTIVATE YOUR FEED <ArrowRight size={14} /></Link>
          </div>
          <div className="space-y-3">
            {feed.map((f) => (
              <div key={f.id} className="card-surface p-4 flex gap-4 items-start hover:border-slate-600 transition-colors" data-testid={`feed-item-${f.id}`}>
                <span className="shrink-0 font-mono text-sm font-semibold text-gold bg-gold/10 border border-gold/30 rounded-sm px-2 py-1">{f.match}%</span>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-techblue-light">{f.kind}</p>
                  <p className="text-sm text-slate-200 mt-1 leading-relaxed">{f.text}</p>
                  <p className="text-xs text-slate-500 mt-1.5 italic">"{f.why}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTELLIGENCE */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="intelligence-section">
        <SectionHeading kicker="Business Intelligence" title={"SEE WHERE BUSINESS IS MOVING."}
          sub="Industry demand, buyer intent, geographic signals — clearly separated into verified data and AI-generated estimates." testid="intelligence-heading" />
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-surface p-6 md:p-8" data-testid="demand-chart-card">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Demand vs Supply Index — Verified Data</p>
              <TrendingUp size={16} className="text-gold" />
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={intel?.demand_trend || []}>
                  <XAxis dataKey="month" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 4, fontSize: 12 }} />
                  <Area type="monotone" dataKey="buyer_demand" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.12} strokeWidth={2} name="Buyer demand" />
                  <Area type="monotone" dataKey="supplier_availability" stroke="#60A5FA" fill="#60A5FA" fillOpacity={0.08} strokeWidth={2} name="Supplier availability" />
                  <Area type="monotone" dataKey="opportunity_volume" stroke="#10B981" fill="#10B981" fillOpacity={0.06} strokeWidth={1.5} name="Opportunity volume" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card-surface hairline-gold p-6 md:p-8" data-testid="market-signal-card">
            <p className="text-[10px] font-mono uppercase tracking-widest text-amber-400">Market Signal · AI Estimate</p>
            <h3 className="font-display text-xl font-bold text-slate-100 mt-3">{intel?.market_signal?.title || "Rising demand detected"}</h3>
            <p className="text-gold font-semibold text-sm mt-1">{intel?.market_signal?.category || "Commercial Kitchen Equipment"} <span className="text-emerald-400 font-mono text-xs ml-2">{intel?.market_signal?.mom_change} MoM</span></p>
            <div className="mt-5 space-y-4 text-sm">
              <div>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-widest mb-2">Top Locations</p>
                <div className="flex flex-wrap gap-2">{(intel?.market_signal?.top_locations || []).map((l) => <span key={l} className="bg-navy-800 border border-slate-700 rounded-sm px-2.5 py-1 text-xs text-slate-300">{l}</span>)}</div>
              </div>
              <div>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-widest mb-2">Top Buyer Segments</p>
                <div className="flex flex-wrap gap-2">{(intel?.market_signal?.buyer_segments || []).map((l) => <span key={l} className="bg-navy-800 border border-slate-700 rounded-sm px-2.5 py-1 text-xs text-slate-300">{l}</span>)}</div>
              </div>
            </div>
            <p className="mt-5 text-[10px] text-slate-600">{intel?.market_signal?.confidence}</p>
            <Link to="/intelligence" data-testid="open-intelligence-btn" className="btn-outline mt-6 w-full text-xs">OPEN INTELLIGENCE HUB</Link>
          </div>
        </div>
      </section>

      {/* NETWORK PREVIEW */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="network-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="gold-kicker mb-4">B2B Network</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">BUILD YOUR BUSINESS NETWORK.<br />INTELLIGENTLY.</h2>
            <p className="mt-5 text-slate-400 leading-relaxed">A commercial relationship network — not a social network. Buyers, suppliers, manufacturers, distributors, investors, lenders, advisors and partners, mapped around your business.</p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[["Buyers", "34"], ["Suppliers", "21"], ["Manufacturers", "18"], ["Distributors", "26"], ["Investors", "9"], ["Advisors", "7"]].map(([k, v]) => (
                <div key={k} className="card-surface px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-slate-300">{k}</span>
                  <span className="font-mono text-gold text-sm">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <NetworkPreview />
        </div>
      </section>

      {/* COMPANIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="companies-section">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="gold-kicker mb-4">Verified Companies</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">CONNECT WITH VERIFIED BUSINESSES.</h2>
          </div>
          <Link to="/opportunities" data-testid="view-all-companies-btn" className="btn-outline">EXPLORE OPPORTUNITIES <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {companies.map((c) => <CompanyCard key={c.id} c={c} />)}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <SectionHeading kicker="Industry Ecosystems" title={"EXPLORE THE BUSINESS WORLD."}
            sub="Each industry opens a dedicated ecosystem — companies, buyers, suppliers, requirements, opportunities, market intelligence and AI matches." testid="industries-heading" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((i) => (
              <Link key={i.name} to={user ? `/opportunities?q=${encodeURIComponent(i.name)}` : "/register"} data-testid={`industry-tile-${i.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className="card-surface p-5 hover:border-gold/50 transition-colors group">
                <Building2 size={18} className="text-gold mb-3" />
                <p className="font-display font-semibold text-slate-200 group-hover:text-gold transition-colors text-sm">{i.name}</p>
                <p className="text-[11px] font-mono text-slate-500 mt-1.5">{i.companies.toLocaleString()} companies · {i.opportunities} opportunities</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL EXPLORER */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="global-explorer-section">
        <SectionHeading kicker="Global Market Explorer" title={"WHERE BUSINESS MEETS OPPORTUNITY.\nACROSS EVERY MARKET."} testid="global-explorer-heading" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
          {(pubStats?.regions || REGION_NAMES.map((n) => ({ name: n, businesses: 0 }))).map((r) => (
            <Link key={r.name} to={guardReg(`/opportunities?location=${encodeURIComponent(r.name === "Middle East" ? "UAE" : r.name)}`)} data-testid={`region-${r.name.toLowerCase().replace(/\s/g, "-")}`}
              className="card-surface p-5 text-center hover:border-gold/50 transition-colors group">
              <Globe2 size={18} className="text-techblue-light mx-auto mb-3" />
              <p className="font-display font-semibold text-slate-200 text-sm group-hover:text-gold transition-colors">{r.name}</p>
              <p className="text-[10px] font-mono text-slate-500 mt-2">{r.businesses} verified businesses</p>
              <p className="text-[10px] font-mono text-gold">{pubStats ? "VERIFIED DATA" : "LOADING…"}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* EVENTS */}
      <section className="border-y border-slate-800 bg-navy-900/40" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="gold-kicker mb-4">Exhibition & Event Intelligence</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">WHERE BUSINESS MEETS BUSINESS.</h2>
            </div>
            <Link to="/events" data-testid="view-all-events-btn" className="btn-outline">ALL EVENTS <ArrowRight size={14} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((e) => <EventCard key={e.id} e={e} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 opacity-40"><NetworkCanvas /></div>
        <div className="absolute inset-0 bg-navy-950/70" />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-32 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-100 leading-tight">
            YOUR NEXT BUSINESS OPPORTUNITY<br />MAY ALREADY BE IN THE NETWORK.
          </h2>
          <Link to="/join" data-testid="final-join-btn" className="btn-gold mt-10 !px-10 !py-4 text-base">JOIN 10XGLOBAL <ArrowRight size={18} /></Link>
          <div className="mt-12" data-testid="home-all-options">
            <p className="gold-kicker mb-5">Explore All 10XGLOBAL Options</p>
            <div className="flex flex-wrap justify-center gap-2.5">
              {[["Opportunities", "/opportunities"], ["Industries", "/industries"], ["Events", "/events"], ["10XEXIM", "/members"],
                ["Membership", "/membership"], ["Intelligence Hub", "/intelligence"], ["Capital Advisory", "/capital-advisory"], ["Post Requirement", "/rfq"],
                ["About", "/about"], ["Contact", "/contact"]].map(([label, href]) => (
                <Link key={href} to={href} data-testid={`home-option-${href.slice(1).replace(/\//g, "-")}`}
                  className="px-4 py-2 text-[11px] font-mono uppercase tracking-widest text-slate-300 border border-slate-700 rounded-sm hover:border-gold/60 hover:text-gold transition-colors">
                  {label}
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-16 pt-10 border-t border-slate-800/70">
            <p className="font-display font-black text-2xl tracking-wide text-slate-100">10XGLOBAL</p>
            <p className="gold-kicker mt-2">The B2B Intelligence & Growth Network</p>
            <p className="text-sm text-slate-400 mt-3">Discover. Connect. Convert. Grow 10X.</p>
            <p className="text-xs text-slate-600 mt-1.5">Verified businesses. Intelligent matching. Real opportunities.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function NetworkPreview() {
  const cats = ["Buyers", "Suppliers", "Manufacturers", "Distributors", "Investors", "Advisors"];
  return (
    <div className="relative mx-auto w-full max-w-md aspect-square" data-testid="network-preview-graph">
      <svg viewBox="0 0 400 400" className="w-full h-full">
        {cats.map((c, i) => {
          const angle = (i / cats.length) * Math.PI * 2 - Math.PI / 2;
          const x = 200 + Math.cos(angle) * 150;
          const y = 200 + Math.sin(angle) * 150;
          return (
            <g key={c}>
              <line x1="200" y1="200" x2={x} y2={y} stroke="#D4AF37" strokeOpacity="0.35" strokeWidth="1" />
              <circle cx={x} cy={y} r="26" fill="#0F172A" stroke="#1E293B" strokeWidth="1.5" />
              <text x={x} y={y + 3} textAnchor="middle" fill="#94A3B8" fontSize="9" fontFamily="JetBrains Mono" style={{ textTransform: "uppercase" }}>{c}</text>
              {[0.35, 0.65].map((t) => (
                <circle key={t} cx={200 + (x - 200) * t} cy={200 + (y - 200) * t} r="3" fill="#60A5FA" fillOpacity="0.7" />
              ))}
            </g>
          );
        })}
        <circle cx="200" cy="200" r="34" fill="#070B14" stroke="#D4AF37" strokeWidth="2" />
        <text x="200" y="197" textAnchor="middle" fill="#D4AF37" fontSize="11" fontWeight="700">YOUR</text>
        <text x="200" y="211" textAnchor="middle" fill="#D4AF37" fontSize="11" fontWeight="700">COMPANY</text>
      </svg>
    </div>
  );
}
